import {
  Component,
  OnInit,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators
} from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyPipe } from '@angular/common';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { AdjustmentType } from '../../models/adjustment-type.enum';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { OnyxEvent } from '../../models/event';
import { PaymentStore } from '../../service/payment.store';
import { AdjustmentStore } from '../../service/adjustment.store';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { LogService } from '../../../shared/log/log.service';
import { PioneerValidators } from '../../../shared/utils/pioneer.validators';
import { Payment } from '../../models/payment';
import { AdjustmentUtils } from '../../utils/adjustment-utils';
import { Adjustment } from '../../models/adjustment';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { EventStore } from '../../service/event.store';
import { EventUtils } from '../../utils/event-utils';

@Component({
  selector: 'pioneer-add-payment',
  templateUrl: './add-payment.component.html'
})
export class AddPaymentComponent implements OnInit, OnChanges {

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  paymentForm: FormGroup;
  adjustmentForm: FormGroup;

  addPaymentEnabled = true;

  // this property exists so that the template can use the AdjustmentType enum
  adjustmentTypes = AdjustmentType;

  confirmationModalConfig = new ConfirmationModalConfig();

  ui = {
    showDescription: false,
    showAuthorizePaymentButton: false
  };

  onyxEvent: OnyxEvent;

  constructor(
    private formBuilder: FormBuilder,
    private paymentStore: PaymentStore,
    private adjustmentStore: AdjustmentStore,
    private translateService: TranslateService,
    private environment: EnvironmentConfiguration,
    private errorHandlerService: ErrorHandlerService,
    private logService: LogService,
    private currencyPipe: CurrencyPipe,
    private eventStore: EventStore
  ) {}

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {

    this.paymentForm = this.formBuilder.group({
      'description': [ '', Validators.maxLength(255) ]
    });

    this.adjustmentForm = this.formBuilder.group({
      // defaults to undefined so that someone is required to enter an amount before submitting
      'adjustmentAmount': [ undefined, [ Validators.required, PioneerValidators.isValidCurrency ] ]
    });

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'AddPaymentComponent'
        })
      );
    }

    this.autoPaymentUI();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (!changes[ 'event' ]) {
      return;
    }

    this.autoPaymentUI();
  }

  isValidPaymentDate(): boolean {
    if (!this.onyxEvent || !this.onyxEvent.endDate) {
      return false;
    }

    const firstPayDate = new Date(this.onyxEvent.endDate);
    firstPayDate.setDate(firstPayDate.getDate() + this.environment.payments.paymentDelayDaysAfterEventEnd + 1);

    const firstPayDateUtcSeconds = firstPayDate.getTime();
    const currentUtcSeconds = new Date().getTime();
    const isValid = currentUtcSeconds >= firstPayDateUtcSeconds;

    this.logService.verbose(`allow: ${firstPayDateUtcSeconds}, current: ${currentUtcSeconds}, isValid: ${isValid}`);

    return isValid;
  }

  confirmBeforeAuthorizingPayment(): void {
    // set up the title of the confirmation modal
    this.calculatedPayment.map((calculatedPayment: number) => {
      const label = 'EVENT.COMMISSION.AUTHORIZE_PAYMENT_CONFIRMATION_TITLE';
      const currencyFormat = this.currencyPipe.transform(
        calculatedPayment,
        'USD',
        true
      );
      const params = { paymentAmount: currencyFormat };
      const title = this.translateService.instant(label, params);
      this.confirmationModalConfig.title = title;
    }).subscribe();

    this.confirmationModal.open();
  }

  addPayment(): Observable<any> {
    const obs = this.calculatedPayment.first().flatMap((commissionAmount: number) => {
      const description = this.paymentForm.value.description;
      const payment = new Payment({
        commissionAmount: commissionAmount,
        description: description,
        currency: 'USD',
        eventId: this.onyxEvent.eventId
      });

      this.addPaymentEnabled = false;
      return this.paymentStore.addPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    }).map(() => {
      this.resetPaymentForm();
      const eventId = this.onyxEvent.eventId;

      // firing off both calls because both have impacts elsewhere:
      //   CommissionComponent and PaymentEquationComponent
      this.paymentStore.loadPayments(eventId).subscribe();
      this.paymentStore.loadLatestPayment(eventId).subscribe();
    });

    obs.subscribe();
    return obs;
  }

  isAddPaymentEnabled(): Observable<boolean> {
    return this.calculatedPayment.flatMap((currentCalculatedPayment) => {
      return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
        return this.paymentStore.isCurrentPaymentValid.map(currentPaymentValid => {
          return this.addPaymentEnabled &&
            currentCalculatedPayment > 0 &&
            !currentPaymentValid &&
            this.isValidPaymentDate() &&
            this.paymentForm.valid &&
            EventUtils.doesEventHaveAgency(event);
        });
      });
    });
  }

  resetPaymentForm(): void {
    this.paymentForm.reset({
      description: ''
    });
  }

  addAdjustment(adjustmentType: AdjustmentType): Observable<any> {
    if (this.adjustmentForm.valid && this.adjustmentForm.value.adjustmentAmount > 0) {
      let adjustmentAmount = this.adjustmentForm.value.adjustmentAmount;
      if (!AdjustmentUtils.isAdjustmentTypePositive(adjustmentType)) {
        adjustmentAmount = -adjustmentAmount;
      }

      const adjustment = new Adjustment({
        eventId: this.onyxEvent.eventId,
        adjustmentAmount: adjustmentAmount,
        adjustmentType: adjustmentType
      });

      // this.adjustmentForm.disable();
      const obs = this.adjustmentStore.addAdjustment(adjustment).finally(() => {
        this.adjustmentForm.enable();
      }).map(() => {
        this.resetAdjustmentForm();
        this.adjustmentStore.loadAdjustments(this.onyxEvent.eventId).subscribe();
      });
      obs.subscribe();
      return obs;
    } else {
      return Observable.of(false);
    }
  }

  resetAdjustmentForm(): void {
    this.adjustmentForm.reset({
      adjustmentAmount: undefined,
    });
  }

  confirmBeforeAddPaymentHandler(value) {
    if (value) {
      this.addPayment();
    }
  }

  private get calculatedPayment(): Observable<number> {
    return this.adjustmentStore.getCalculatedPayment(ReservationStatusCode.CHECKED_OUT)
  }

  private autoPaymentUI() {
    const autoPayment = this.onyxEvent.autoPayment;

    if ( autoPayment === true) {
      this.ui.showDescription = false;
      this.ui.showAuthorizePaymentButton = false;
    } else {
      this.ui.showDescription = true;
      this.ui.showAuthorizePaymentButton = true;
    }
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/payment/add-payment/add-payment.component.ts